import type { Unsubscribe } from "../platform/Platform";
import {
    APPEARANCE_STORAGE_KEY,
    DEFAULT_APPEARANCE_SETTINGS,
    parseAppearanceSettings,
    parsePersistedAppearanceSettings,
    serializeAppearanceSettings,
    type Appearance,
    type AppearanceSettings,
} from "./appearance";

export type { Appearance, Unsubscribe };

type Listener = (appearance: Appearance) => void;

/** The persisted choice; where storage cannot be read (blocked site data, a private window, a
 *  page with no storage of its own) the defaults, never a throw while the app's modules load. */
function readPersistedSettings(): AppearanceSettings {
    if (typeof window === "undefined") return { ...DEFAULT_APPEARANCE_SETTINGS };
    let raw: string | null = null;
    try {
        raw = window.localStorage.getItem(APPEARANCE_STORAGE_KEY);
    } catch {
        raw = null;
    }
    return parsePersistedAppearanceSettings(raw);
}

function toAppearance(source: AppearanceSettings): Appearance {
    return {
        textSize: source.textSize,
        density: source.density,
    };
}

let settings = readPersistedSettings();
// `useSyncExternalStore` needs a stable reference between changes (`Object.is`
// against the previous snapshot): a fresh object literal on every `getAppearance()`
// call would look like a change on every render and loop forever.
let appearance = toAppearance(settings);
const listeners = new Set<Listener>();

function replaceSettings(next: AppearanceSettings): boolean {
    if (next.textSize === settings.textSize && next.density === settings.density) return false;
    settings = next;
    appearance = toAppearance(next);
    listeners.forEach((listener) => listener(appearance));
    return true;
}

// This store owns the persisted text-size/density preference (color mode is
// Joy's own concern; see appearance.ts). Browsers notify other tabs through `storage`.
if (typeof window !== "undefined" && typeof window.addEventListener === "function") {
    window.addEventListener("storage", (event) => {
        if (event.key !== APPEARANCE_STORAGE_KEY) return;
        replaceSettings(parsePersistedAppearanceSettings(event.newValue));
    });
}

/** The current appearance: text size and density. Stable across calls
 *  until `setAppearance` actually changes a field. */
export function getAppearance(): Appearance {
    return appearance;
}

/** Patches, persists to `localStorage` and notifies subscribers. A no-op patch is
 *  silent: no persistence write, no listener call. */
export function setAppearance(patch: Partial<Appearance>): void {
    const next = parseAppearanceSettings({
        textSize: patch.textSize ?? settings.textSize,
        density: patch.density ?? settings.density,
    });
    if (!replaceSettings(next)) return;
    if (typeof window === "undefined") return;
    try {
        window.localStorage.setItem(APPEARANCE_STORAGE_KEY, serializeAppearanceSettings(next));
    } catch {
        // Storage that cannot be written: the choice holds for this page only.
    }
}

/** Notifies on every `setAppearance` change. Returns the unsubscribe function. */
export function subscribeAppearance(listener: Listener): Unsubscribe {
    listeners.add(listener);
    return () => listeners.delete(listener);
}
